import React, {FC} from 'react'
import {Grid, Tabs, TabsAlignment, TabsSkin, TabsVariant, Text} from 'wix-ui-tpa/cssVars'
import {useEnvironment, WidgetProps} from '@wix/yoshi-flow-editor'
import {useSettings, useStyles} from '@wix/tpa-settings/react'

import {stylesParams} from '../stylesParams'
import {settingsParams} from '../settingsParams'
import {LAYOUT_BREAKPOINTS_START} from '../constants'
import {withExperiencesListStorageProvider} from '../storage'
import {Breadcrumbs} from '../../../components-shared/Breadcrumbs'
import {
  useGetLayoutSize,
  withLayoutSizeProvider,
} from '../../../components-shared/LayoutSizeProvider'
import {Alignment} from '../../../types/createStylesParams'
import {LayoutSize} from '../../../components-shared/LayoutSizeProvider/types'

import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'
import {experiencesListDataHooks} from './constants'
import {Experience} from './components/Experience'
import {EmptyState} from './components/EmptyState'

const Widget: FC<WidgetProps> = () => {
  const {isMobile} = useEnvironment()
  const {layoutSize} = useGetLayoutSize()
  const settings = useSettings()
  const styles = useStyles()

  const titleText: string = settings.get(settingsParams.titleText)
  const allReservationLocationsTabText: string = settings.get(settingsParams.allLocationsTabText)

  const {
    regionalSettings,
    breadcrumbsConfig,
    selectedReservationLocationTabIndex,
    reservationLocationTabOptions,
    selectedExperiences,
    showEmptyState,
    handleReservationLocationSelect,
    handleGoToExperience,
  } = useHooks({allReservationLocationsTabText})

  const showBreadcrumbs: boolean = settings.get(settingsParams.showBreadcrumbs)
  const showTitle: boolean = settings.get(settingsParams.showTitle)
  const showLocationTabs: boolean = settings.get(settingsParams.showLocationTabs)

  const breadcrumbsAlignment: Alignment = styles.get(stylesParams.breadcrumbsAlignment)
  const locationTabsAlignment: Alignment = styles.get(stylesParams.locationTabsAlignment)
  const locationTabsAlignmentProp: TabsAlignment =
    ReservationLocationsTabsAlignment[locationTabsAlignment]
  const fitLocationTabsWidthToListWidth: boolean = isMobile
    ? true
    : styles.get(stylesParams.fitLocationTabsWidthToListWidth) === 1

  const maximumExperiencesPerRow: number = styles.get(stylesParams.maximumExperiencesPerRow)
  const spaceBetweenExperiences: number = styles.get(stylesParams.spaceBetweenExperiences)

  const showReservationLocationTabsOverride = showLocationTabs
    ? reservationLocationTabOptions.length > 2
    : false

  const desktopMaximumExperiencesGridColumns =
    selectedExperiences.length >= maximumExperiencesPerRow
      ? maximumExperiencesPerRow
      : selectedExperiences.length
  const maximumExperiencesGridColumns =
    isMobile && typeof MaximumExperiencesGridColumnsForMobileLayout[layoutSize] !== 'undefined'
      ? MaximumExperiencesGridColumnsForMobileLayout[layoutSize]
      : desktopMaximumExperiencesGridColumns

  const showExperienceCoverImages = selectedExperiences.some(
    ({configuration}) => !!configuration?.displayInfo?.coverImage?.id,
  )

  const renderExperiences = () =>
    showEmptyState ? (
      <EmptyState />
    ) : (
      <Grid
        className={st(classes.experiencesGrid)}
        maxColumns={maximumExperiencesGridColumns}
        rowGap={spaceBetweenExperiences}
        columnGap={spaceBetweenExperiences}
      >
        {selectedExperiences.map((experience) => (
          <Grid.Item key={experience.id} colSpan={1} rowSpan={1}>
            <Experience
              regionalSettings={regionalSettings}
              experience={experience}
              showCoverImages={showExperienceCoverImages}
              handleGoToExperience={handleGoToExperience}
            />
          </Grid.Item>
        ))}
      </Grid>
    )

  return (
    <div
      className={st(classes.root, {[layoutSize]: true})}
      data-hook={experiencesListDataHooks.root()}
    >
      {showBreadcrumbs ? (
        <Breadcrumbs
          items={breadcrumbsConfig}
          className={st(classes.breadcrumbs)}
          alignment={breadcrumbsAlignment}
        />
      ) : null}

      {showTitle ? (
        <Text tagName="h1" className={st(classes.title)}>
          {titleText}
        </Text>
      ) : null}

      {showReservationLocationTabsOverride ? (
        <Tabs
          className={st(classes.reservationLocationsTabs)}
          activeTabIndex={selectedReservationLocationTabIndex}
          items={reservationLocationTabOptions}
          onTabClick={handleReservationLocationSelect}
          skin={TabsSkin.fitUnderline}
          alignment={locationTabsAlignmentProp}
          variant={fitLocationTabsWidthToListWidth ? TabsVariant.fullWidth : TabsVariant.fit}
          isTablist
          aria-label={titleText}
        />
      ) : null}

      {showReservationLocationTabsOverride
        ? reservationLocationTabOptions.map(({id}, index) => {
            const tabId = id!

            return (
              <div
                key={tabId}
                id={`${tabId}-tab`}
                role="tabpanel"
                aria-labelledby={tabId}
                hidden={index !== selectedReservationLocationTabIndex}
              >
                {index === selectedReservationLocationTabIndex ? renderExperiences() : null}
              </div>
            )
          })
        : renderExperiences()}
    </div>
  )
}

export default withLayoutSizeProvider(
  withExperiencesListStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)

const ReservationLocationsTabsAlignment: Record<
  Exclude<Alignment, Alignment.Stretch>,
  TabsAlignment
> = {
  [Alignment.Left]: TabsAlignment.left,
  [Alignment.Center]: TabsAlignment.center,
  [Alignment.Right]: TabsAlignment.right,
}

const MaximumExperiencesGridColumnsForMobileLayout: Record<LayoutSize.XS | LayoutSize.S, number> = {
  [LayoutSize.XS]: 1,
  [LayoutSize.S]: 2,
}
